import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';

/**
 * **Forget everything loaded for an account the moment the signed-in account changes**
 * (`USR-007` AC-3; TW 44175697, TW 44175703).
 *
 * Log out navigates to sign-in without reloading the page, and nothing emptied the query cache —
 * whose keys name what was read (`['messages', 'conversations']`), never who read it. So the next
 * person to sign in on the same tab was shown the previous account's conversations until they
 * refreshed, and opening one was refused by the server as *"not one of yours"*. The server never
 * served one account's data to another; the browser kept it on screen.
 *
 * `accountId` is the signed-in user's id, `null` when signed out, and `undefined` while the
 * session is still being settled. The first settled value is where the cache began and is not a
 * change; every later difference — A to signed out, signed out to B, or A straight to B — removes
 * every query. Public reads (site settings, Discover) are removed too and simply load again, which
 * is the price of not having to decide, page by page, what counts as an account's data.
 */
export function AccountDataBoundary({
  accountId,
}: {
  readonly accountId: string | null | undefined;
}): null {
  const queryClient = useQueryClient();
  const settled = React.useRef<{ readonly accountId: string | null } | null>(null);

  React.useEffect(() => {
    if (accountId === undefined) return;
    const previous = settled.current;
    settled.current = { accountId };
    if (previous === null || previous.accountId === accountId) return;
    queryClient.removeQueries();
  }, [accountId, queryClient]);

  return null;
}
